import { useState, useEffect, useCallback } from 'react';
import { GameClient } from '../services/GameClient';
import type { 
  GameState, 
  Player, 
  Card,
  GamePhaseType,
  SuitType
} from '../types/game';
import type { 
  PlayedCard 
} from '../../../shared/src/types';

interface UseKadiNiTidiGameReturn {
  // Connection state
  isConnected: boolean;
  isConnecting: boolean;
  connectionError: string | null;
  
  // Game state
  gameState: GameState | null;
  currentPlayer: Player | null;
  players: Player[];
  currentPhase: GamePhaseType;
  
  // Partnership config (from room state)
  partnershipConfig?: string;
  
  // Player's hand and game info
  playerCards: Card[];
  currentTrump: SuitType | null;
  currentTrick: PlayedCard[];
  scores: Record<string, number>;
  
  // Actions
  connectToGame: (username: string, roomId?: string, enableAI?: boolean, authenticatedUserId?: string) => Promise<void>;
  joinSpecificRoom: (roomId: string, username: string, authenticatedUserId?: string) => Promise<void>;
  disconnect: () => void;
  makeBid: (bid: number, powerHouse: SuitType, partnerCards: Card[]) => void;
  passMove: () => void;
  playCard: (card: Card) => void;
  setReady: (ready: boolean) => void;
  requestAIPlayers: () => void;
  addAIPlayer: () => void;
  removeAIPlayer: () => void;
  restartGame: () => void;
  toggleAutoPlay: (enabled: boolean) => void;
  startFreshSession: () => void;
  
  // Room info
  roomInfo: { roomId: string; playerCount: number; frontendRoomId: string | null } | null;
  
  // Error handling
  setGameErrorHandler: (handler: (error: string) => void) => void;
}

export const useKadiNiTidiGame = (): UseKadiNiTidiGameReturn => {
  const [gameClient] = useState(() => new GameClient());
  const [gameState, setGameState] = useState<GameState | null>(null);
  const [partnershipConfig, setPartnershipConfig] = useState<string | undefined>(undefined);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [gameErrorHandler, setGameErrorHandler] = useState<((error: string) => void) | null>(null);

  // Setup game client callbacks
  useEffect(() => {
    gameClient.setCallbacks({
      onGameStateUpdate: (state: GameState) => {
        console.log('Game state updated:', state);
        setGameState(state);
      },
      onError: (error: string) => {
        console.error('Game error:', error);
        
        // Differentiate between connection errors and game action errors
        const isGameActionError = error.includes('Invalid bid') || 
                                  error.includes('Invalid card play') ||
                                  error.includes('Not your turn') || 
                                  error.includes('Cannot play') ||
                                  error.includes('Invalid move') ||
                                  error.includes('Game action error');
        
        if (isGameActionError && gameErrorHandler) {
          // Handle as game action error with notification
          gameErrorHandler(error);
        } else {
          // Handle as connection error
          setConnectionError(error);
        }
      },
      onRoomStateUpdate: (state) => {
        console.log('Room state updated:', state);
        setIsConnected(true);
        // Extract partnership config from room state
        const config = gameClient.getPartnershipConfig();
        setPartnershipConfig(config);
      }
    });
  }, [gameClient, gameErrorHandler]);

  // Connect to game room
  const connectToGame = useCallback(async (username: string, roomId?: string, enableAI: boolean = false, authenticatedUserId?: string) => {
    console.log('🎮 connectToGame called with:', { username, roomId, enableAI, authenticatedUserId });
    setIsConnecting(true);
    setConnectionError(null);
    
    try {
      // Use authenticated user ID if available, otherwise get/generate persistent temporary ID
      let userId = authenticatedUserId;
      if (!userId) {
        // Check localStorage for existing temporary ID
        const storedTempId = localStorage.getItem('temp_user_id');
        if (storedTempId) {
          userId = storedTempId;
          console.log('🔄 Using stored temporary userId:', userId);
        } else {
          // Generate new temporary ID and store it
          userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('temp_user_id', userId);
          console.log('🆕 Generated new temporary userId:', userId);
        }
      } else {
        console.log('🔄 Using authenticated userId:', userId);
        // Clear any temporary ID when user is authenticated
        localStorage.removeItem('temp_user_id');
      }
      
      if (roomId) {
        console.log('🎯 Joining specific room:', roomId);
        await gameClient.joinRoomById(roomId, { userId, username });
      } else {
        console.log('🎯 Creating/joining kadi-ni-tidi room');
        await gameClient.joinKadiNiTidiRoom({ userId, username }, enableAI);
      }
      
      console.log('✅ Connection successful');
      setIsConnected(true);
      
    } catch (error) {
      console.error('❌ Failed to connect to game:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to connect');
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  }, [gameClient]);

  // Join specific room by ID
  const joinSpecificRoom = useCallback(async (roomId: string, username: string, authenticatedUserId?: string) => {
    console.log('🎮 joinSpecificRoom called with:', { roomId, username, authenticatedUserId });
    setIsConnecting(true);
    setConnectionError(null);
    
    try {
      // Use authenticated user ID if available, otherwise get/generate persistent temporary ID
      let userId = authenticatedUserId;
      if (!userId) {
        // Check localStorage for existing temporary ID
        const storedTempId = localStorage.getItem('temp_user_id');
        if (storedTempId) {
          userId = storedTempId;
          console.log('🔄 Using stored temporary userId:', userId);
        } else {
          // Generate new temporary ID and store it
          userId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('temp_user_id', userId);
          console.log('🆕 Generated new temporary userId:', userId);
        }
      } else {
        console.log('🔄 Using authenticated userId:', userId);
        // Clear any temporary ID when user is authenticated
        localStorage.removeItem('temp_user_id');
      }
      console.log('🎯 Joining room by ID:', roomId);
      
      await gameClient.joinRoomById(roomId, { userId, username });
      
      console.log('✅ Room join successful');
      setIsConnected(true);
    } catch (error) {
      console.error('❌ Failed to join specific room:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to join room');
      setIsConnected(false);
    } finally {
      setIsConnecting(false);
    }
  }, [gameClient]);

  // Request AI players for single-player mode (legacy method)
  const requestAIPlayers = useCallback(async () => {
    try {
      await gameClient.requestAIPlayers();
    } catch (error) {
      console.error('Failed to request AI players:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to add AI players');
    }
  }, [gameClient]);

  // Add a single AI player
  const addAIPlayer = useCallback(() => {
    try {
      gameClient.addAIPlayer();
    } catch (error) {
      console.error('Failed to add AI player:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to add AI player');
    }
  }, [gameClient]);

  // Remove an AI player
  const removeAIPlayer = useCallback(() => {
    try {
      gameClient.removeAIPlayer();
    } catch (error) {
      console.error('Failed to remove AI player:', error);
      setConnectionError(error instanceof Error ? error.message : 'Failed to remove AI player');
    }
  }, [gameClient]);

  // Disconnect from game room
  const disconnect = useCallback(() => {
    gameClient.leaveRoom();
    setIsConnected(false);
    setGameState(null);
    setConnectionError(null);
    // Note: We DON'T clear temp_user_id here to allow reconnection with same ID
  }, [gameClient]);

  // Start fresh session (new room) - clears temporary user ID
  const startFreshSession = useCallback(() => {
    localStorage.removeItem('temp_user_id');
    console.log('🆕 Cleared temporary user ID for fresh session');
  }, []);

  // Game actions
  const makeBid = useCallback((bid: number, powerHouse: SuitType, partnerCards: Card[]) => {
    gameClient.sendBid(bid, powerHouse, partnerCards);
  }, [gameClient]);

  const passMove = useCallback(() => {
    console.log('🚫 passMove called - sending pass to server');
    gameClient.sendPass();
  }, [gameClient]);

  const playCard = useCallback((card: Card) => {
    gameClient.playCard(card);
  }, [gameClient]);

  const setReady = useCallback((ready: boolean) => {
    gameClient.setReady(ready);
  }, [gameClient]);

  const restartGame = useCallback(() => {
    gameClient.restartGame();
  }, [gameClient]);

  const toggleAutoPlay = useCallback((enabled: boolean) => {
    gameClient.toggleAutoPlay(enabled);
  }, [gameClient]);

  // Set up game error handler
  const handleSetGameErrorHandler = useCallback((handler: (error: string) => void) => {
    setGameErrorHandler(() => handler);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (gameClient.isConnected()) {
        gameClient.leaveRoom();
      }
    };
  }, [gameClient]);

  // Derived state - with proper fallbacks
  const currentUserId = gameClient.getCurrentUserId();
  
  // DEBUG: Log the matching process
  console.log('🔍 [useKadiNiTidiGame] Player Matching Debug:', {
    currentUserId,
    playersCount: gameState?.players?.length || 0,
    players: gameState?.players?.map(p => ({ 
      id: p.id, 
      _id: (p as any)._id, 
      name: p.name 
    }))
  });
  
  // FIX: Defensive ID matching - handle both id and _id fields to prevent NULL currentPlayer errors
  const currentPlayer = gameState?.players?.find((p: Player) => {
    const matchById = p.id === currentUserId;
    const matchBy_id = (p as any)._id === currentUserId;
    const matched = matchById || matchBy_id;
    
    if (matched) {
      console.log('✅ [useKadiNiTidiGame] Found currentPlayer:', { 
        playerId: p.id, 
        player_id: (p as any)._id,
        matchedBy: matchById ? 'id' : '_id',
        playerName: p.name 
      });
    }
    
    return matched;
  }) || null;
  
  if (!currentPlayer && gameState?.players && gameState.players.length > 0) {
    console.error('❌ [useKadiNiTidiGame] FAILED to find currentPlayer!', {
      currentUserId,
      availablePlayers: gameState.players.map(p => ({
        id: p.id,
        _id: (p as any)._id,
        name: p.name
      }))
    });
  }
  
  const players = gameState?.players || [];
  const currentPhase: GamePhaseType = gameState?.phase || 'waiting_for_players';
  const playerCards = currentPlayer?.hand || [];
  const currentTrump: SuitType | null = null; // Will be updated when backend provides trump
  const currentTrick: PlayedCard[] = []; // Will be updated when backend provides current trick
  const scores: Record<string, number> = {}; // Will be updated when backend provides scores
  const roomInfo = gameClient.getRoomInfo();

  return {
    isConnected,
    isConnecting,
    connectionError,
    gameState,
    partnershipConfig, // Expose partnership config from room state
    currentPlayer,
    players,
    currentPhase,
    playerCards,
    currentTrump,
    currentTrick,
    scores,
    connectToGame,
    disconnect,
    makeBid,
    passMove,
    playCard,
    setReady,
    joinSpecificRoom,
    requestAIPlayers,
    addAIPlayer,
    removeAIPlayer,
    restartGame,
    toggleAutoPlay,
    startFreshSession,
    roomInfo,
    setGameErrorHandler: handleSetGameErrorHandler,
  };
};
